import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Dialog, DialogActions, DialogContent, 
  DialogTitle, TextField, MenuItem, IconButton, Snackbar, Alert, TablePagination
} from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import api from '../../services/api';

const PLAN_TYPES = ['free', 'basic', 'premium', 'enterprise'];

const ClientForm = ({ client, onSave, onCancel }) => {
  const [formData, setFormData] = useState(
    client || {
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      plan: 'free',
      notes: '',
      website: '',
      industry: ''
    }
  );
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            autoFocus
            label="Company Name"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            fullWidth
            required
            margin="dense"
          />
          <TextField
            label="Contact Name"
            name="contact_name"
            value={formData.contact_name}
            onChange={handleChange}
            fullWidth
            required
            margin="dense"
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
            margin="dense"
          />
          <TextField
            label="Phone"
            name="phone"
            value={formData.phone || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Plan"
            name="plan"
            select
            value={formData.plan}
            onChange={handleChange}
            fullWidth
            margin="dense"
          >
            {PLAN_TYPES.map((plan) => (
              <MenuItem key={plan} value={plan}>
                {plan.charAt(0).toUpperCase() + plan.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Industry"
            name="industry"
            value={formData.industry || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Website"
            name="website"
            value={formData.website || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Address"
            name="address"
            value={formData.address || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="City"
            name="city"
            value={formData.city || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="State/Province"
            name="state"
            value={formData.state || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Postal Code"
            name="postal_code"
            value={formData.postal_code || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Country"
            name="country"
            value={formData.country || ''}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Notes"
            name="notes"
            value={formData.notes || ''}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
            margin="dense"
            sx={{ gridColumn: '1 / span 2' }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">Save</Button>
      </DialogActions>
    </form>
  );
};

const AdminClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);
  
  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/admin/clients');
      setClients(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddClient = () => {
    setSelectedClient(null);
    setDialogOpen(true);
  };
  
  const handleEditClient = (client) => {
    setSelectedClient(client);
    setDialogOpen(true);
  };
  
  const handleSaveClient = async (clientData) => {
    try {
      if (selectedClient) {
        // Update existing client
        await api.put(`/v1/admin/clients/${selectedClient.id}`, clientData);
        setSnackbar({
          open: true,
          message: 'Client updated successfully!',
          severity: 'success'
        });
      } else {
        // Add new client
        await api.post('/v1/admin/clients', clientData);
        setSnackbar({
          open: true,
          message: 'Client added successfully!',
          severity: 'success'
        });
      }
      
      setDialogOpen(false);
      fetchClients(); // Refresh client list
    } catch (err) {
      console.error('Error saving client:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save client. Please try again.',
        severity: 'error'
      });
    }
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return <Typography>Loading clients...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Client Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddClient}
        >
          Add Client
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((client) => (
                <TableRow key={client.id}>
                  <TableCell>{client.company_name}</TableCell>
                  <TableCell>{client.contact_name}</TableCell>
                  <TableCell>{client.email}</TableCell>
                  <TableCell>{client.phone || 'N/A'}</TableCell>
                  <TableCell>
                    {client.plan.charAt(0).toUpperCase() + client.plan.slice(1)}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditClient(client)}>
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {clients.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No clients found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={clients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      
      {/* Client Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedClient ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <ClientForm 
          client={selectedClient}
          onSave={handleSaveClient}
          onCancel={handleCloseDialog}
        />
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminClientManagement;